'use client'

import { useState } from 'react'
import { cn } from '../../utils/cn'

export interface AccordionSection {
  id: string
  label: string
  description?: string
  status: 'pending' | 'active' | 'completed' | 'error'
  children?: React.ReactNode
}

export interface AccordionStatusProps {
  sections: AccordionSection[]
  className?: string
}

export function AccordionStatus({ sections, className }: AccordionStatusProps) {
  const [openId, setOpenId] = useState<string | null>(sections.find(s => s.status === 'active')?.id || null)

  return (
    <div className={cn('rounded-xl border border-border bg-card divide-y divide-border', className)}>
      {sections.map(section => {
        const isOpen = openId === section.id
        return (
          <div key={section.id}>
            <button
              onClick={() => setOpenId(isOpen ? null : section.id)}
              className="flex items-center gap-3 w-full px-5 py-4 text-left hover:bg-muted/20 transition-colors"
            >
              <div className={cn(
                'flex items-center justify-center w-6 h-6 rounded-full border-2 shrink-0',
                section.status === 'completed' && 'bg-primary/20 border-primary',
                section.status === 'active' && 'bg-primary border-primary',
                section.status === 'error' && 'bg-destructive/20 border-destructive',
                section.status === 'pending' && 'border-border',
              )}>
                {section.status === 'completed' && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-primary"><polyline points="20 6 9 17 4 12" /></svg>}
                {section.status === 'active' && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
              </div>
              <div className="flex-1">
                <span className={cn('text-sm font-semibold', section.status === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>{section.label}</span>
                {section.description && !isOpen && <p className="text-xs text-muted-foreground truncate">{section.description}</p>}
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                className={cn('text-muted-foreground transition-transform', isOpen && 'rotate-180')}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isOpen && (
              <div className="px-5 pb-4 pl-14">
                {section.description && <p className="text-sm text-muted-foreground mb-3">{section.description}</p>}
                {section.children}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
